/**
 * Route UI della pagina "Tickets" (triage vulnerabilità + workflow Ticket per prodotto).
 *
 * La pagina vive su /tickets; le vecchie route /triage e /triage/:projectUuid restano come
 * redirect (bookmark, link nelle notifiche già inviate). Le API restano invariate
 * (/releases/:uuid/triage, /products/triages/…): qui cambiano SOLO i path del portale.
 */

/** Path della pagina Tickets. */
export const TICKETS_PATH = '/tickets';

/** Prefisso storico della pagina, mantenuto come redirect verso {@link TICKETS_PATH}. */
export const LEGACY_TRIAGE_PATH = '/triage';

/** Path del triage dei finding di un bundle/release DT. */
export function ticketsReleasePath(projectUuid: string): string {
  return `${TICKETS_PATH}/${projectUuid}`;
}

/** Schede della vista index di /tickets, guidate dall'hash della URL. */
export type TicketsTab = 'by-product' | 'per-cve' | 'per-kev';

/** Scheda di default (hash assente o sconosciuto). */
export const DEFAULT_TICKETS_TAB: TicketsTab = 'by-product';

/**
 * Slug storici delle schede → slug correnti ("Tutte le CVE" → "Per CVE", "Dashboard KEV" →
 * "Per KEV"): i link già condivisi/salvati continuano ad aprire la scheda giusta.
 */
const LEGACY_TAB_HASH: Record<string, TicketsTab> = {
  '#all-cves': 'per-cve',
  '#kev': 'per-kev',
};

/** Scheda attiva ricavata dall'hash (accetta anche gli slug storici). */
export function ticketsTabFromHash(hash: string): TicketsTab {
  const legacy = LEGACY_TAB_HASH[hash];
  if (legacy) return legacy;
  if (hash === '#per-cve') return 'per-cve';
  if (hash === '#per-kev') return 'per-kev';
  return DEFAULT_TICKETS_TAB;
}

/** Hash corrente corrispondente a uno slug storico, o null se l'hash non è storico. */
export function currentTabHash(hash: string): string | null {
  const legacy = LEGACY_TAB_HASH[hash];
  return legacy ? `#${legacy}` : null;
}

/** Lista globale delle CVE (scheda #per-cve), con riga della CVE pre-espansa. */
export function ticketsCvePath(cveId: string): string {
  return `${TICKETS_PATH}?cve=${encodeURIComponent(cveId)}#per-cve`;
}

/** Parametro di query del deep link a un singolo Ticket (riga pre-espansa). */
export const TICKET_PARAM = 'ticket';

/** Deep link (path) a un Ticket: apre la scheda "Per prodotto" con il ticket espanso. */
export function ticketPath(ticketId: number): string {
  return `${TICKETS_PATH}?${TICKET_PARAM}=${ticketId}#by-product`;
}

/** Deep link assoluto a un Ticket (da copiare/condividere), sull'origine indicata. */
export function ticketUrl(ticketId: number, origin: string): string {
  return `${origin.replace(/\/$/, '')}${ticketPath(ticketId)}`;
}

/**
 * Traduce un path storico /triage… nel corrispondente /tickets… (query e hash inclusi).
 * Path non storici sono restituiti invariati.
 */
export function legacyTriageRedirectTarget(pathname: string, search = '', hash = ''): string {
  const resto = pathname.startsWith(LEGACY_TRIAGE_PATH)
    ? pathname.slice(LEGACY_TRIAGE_PATH.length)
    : null;
  if (resto === null || (resto !== '' && !resto.startsWith('/'))) return pathname + search + hash;
  return TICKETS_PATH + resto + search + (currentTabHash(hash) ?? hash);
}
